/**
 * "Redefinir senha" (spec 034): confirmação → o servidor gera e grava a senha
 * temporária → ela aparece UMA vez, com "Copiar" e "Copiar mensagem" (texto
 * pronto para o WhatsApp). Fechar descarta: a senha vive só no estado deste
 * diálogo (reducer puro em ./password-reset.ts), nunca no cache do
 * react-query, em storage ou em log.
 *
 * A pessoa da equipe sem conta no SSO (15/10: quem o espelho do TiFlux trouxe e
 * nunca recebeu acesso) recebe "Criar o login e gerar a senha": o "Novo usuário"
 * com o mesmo e-mail, que a adota (F-180: a mesma linha, o vínculo com esta
 * empresa e o login no SSO), e logo depois a mesma redefinição de sempre.
 */
import { useReducer, useState } from 'react';
import { Check, Copy, KeyRound, MessageSquareText } from 'lucide-react';
import type { PasswordResetResponse, User, UserCreateResponse } from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/misc';
import { copyText } from '@/features/vault/vault-clipboard';
import {
  INITIAL_REVEAL,
  offersLoginCreation,
  passwordResetErrorMessage,
  revealReducer,
  whatsappMessage,
} from './password-reset';

export function PasswordResetDialog({
  user,
  notice,
  onClose,
  onLoginCreated,
}: {
  user: Pick<User, 'id' | 'name' | 'email' | 'role'>;
  /** um aviso antes da confirmação — F-180: "esta pessoa já existia no ERP" */
  notice?: string;
  onClose: () => void;
  /** o login foi criado aqui: a lista de usuários se atualiza (quem tem o cache é a página) */
  onLoginCreated?: () => void;
}) {
  const [state, dispatch] = useReducer(revealReducer, INITIAL_REVEAL);
  const [copied, setCopied] = useState<'password' | 'message' | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);

  const close = () => {
    dispatch({ type: 'closed' });
    setCopied(null);
    onClose();
  };

  // chamada direta, fora do react-query: o cache de mutation guardaria a
  // resposta (com a senha) por `gcTime` depois de o diálogo fechar
  const reset = async () => {
    const res = await api<PasswordResetResponse>(`/users/${user.id}/password-reset`, {
      method: 'POST',
    });
    if (res.error) {
      dispatch({ type: 'failed', error: passwordResetErrorMessage(res.error.error), code: res.error.error });
    } else dispatch({ type: 'succeeded', secret: res.data });
  };

  const confirm = async () => {
    dispatch({ type: 'requested' });
    await reset();
  };

  // a mesma linha, adotada: se a resposta for de OUTRA pessoa, não se redefine nada
  const createLogin = async () => {
    dispatch({ type: 'requested' });
    const res = await api<UserCreateResponse>('/users', {
      method: 'POST',
      body: { name: user.name, email: user.email, role: user.role },
    });
    if (res.error) {
      dispatch({ type: 'failed', error: passwordResetErrorMessage(res.error.error), code: res.error.error });
      return;
    }
    if (res.data.id !== user.id) {
      dispatch({
        type: 'failed',
        error: 'O login saiu para outra linha da lista. Nada foi redefinido: recarregue a página e confira.',
      });
      onLoginCreated?.();
      return;
    }
    onLoginCreated?.();
    await reset();
  };

  const createsLogin = offersLoginCreation(state, user);

  const copy = async (what: 'password' | 'message', text: string) => {
    const ok = await copyText(text);
    setCopyFailed(!ok);
    setCopied(ok ? what : null);
  };

  return (
    <Dialog open onClose={close} title="Redefinir senha">
      {state.step === 'shown' ? (
        <div className="space-y-4">
          <p className="text-sm">
            Nova senha de <span className="font-medium">{state.secret.name}</span>{' '}
            <span className="text-muted-foreground">({state.secret.email})</span>:
          </p>
          <code
            data-testid="spec034-senha-temporaria"
            className="block rounded-md border bg-muted px-3 py-2.5 text-center font-mono text-lg tracking-wider select-all"
          >
            {state.secret.temporaryPassword}
          </code>
          <p className="text-xs text-muted-foreground">
            {state.secret.mustChangeOnLogin
              ? 'É temporária: no primeiro acesso a pessoa cria a senha dela. '
              : ''}
            As sessões abertas desta pessoa foram encerradas.{' '}
            <span className="font-medium text-foreground">
              Esta senha não aparece de novo — copie antes de fechar.
            </span>
          </p>
          {copyFailed ? (
            <p className="text-xs text-destructive">
              Não foi possível copiar — selecione o texto e copie à mão.
            </p>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void copy('password', state.secret.temporaryPassword)}
            >
              {copied === 'password' ? <Check className="size-4" /> : <Copy className="size-4" />}
              {copied === 'password' ? 'Copiada' : 'Copiar'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void copy('message', whatsappMessage(state.secret))}
            >
              {copied === 'message' ? (
                <Check className="size-4" />
              ) : (
                <MessageSquareText className="size-4" />
              )}
              {copied === 'message' ? 'Mensagem copiada' : 'Copiar mensagem'}
            </Button>
            <Button type="button" size="sm" onClick={close}>
              Fechar
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {notice ? (
            <p
              data-testid="f180-pessoa-adotada"
              className="rounded-md border border-info/30 bg-info/10 px-3 py-2 text-sm"
            >
              {notice}
            </p>
          ) : null}
          <p className="text-sm">
            Redefinir a senha de <span className="font-medium">{user.name}</span>{' '}
            <span className="text-muted-foreground">({user.email})</span>?
          </p>
          <ul className="list-disc space-y-1 pl-5 text-[13px] text-muted-foreground">
            <li>A senha atual deixa de valer e as sessões abertas são encerradas.</li>
            <li>Uma senha temporária aparece aqui uma única vez, para você enviar à pessoa.</li>
          </ul>
          {state.step === 'failed' ? (
            <p className={createsLogin ? 'text-sm' : 'text-sm text-destructive'}>{state.error}</p>
          ) : null}
          {createsLogin ? (
            <p data-testid="sem-login-sso" className="rounded-md border border-info/30 bg-info/10 px-3 py-2 text-sm">
              Para dar acesso, o ERP cria o login dela no SSO e a inclui na equipe desta empresa. Depois
              gera a senha temporária para você enviar.
            </p>
          ) : null}
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={close}>
              Cancelar
            </Button>
            {createsLogin ? (
              <Button type="button" size="sm" onClick={() => void createLogin()}>
                <KeyRound className="size-4" />
                Criar o login e gerar a senha
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                disabled={state.step === 'working'}
                onClick={() => void confirm()}
              >
                <KeyRound className="size-4" />
                {state.step === 'working' ? 'Redefinindo…' : 'Redefinir senha'}
              </Button>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}
